import {useCallback, useEffect, useRef} from 'react';
import {useLocation} from 'react-router-dom';

import {useAppDispatch, useAppSelector} from '/src/store/hooks';
import {
    fetchEventTypesRequest,
    fetchEventTypesSuccess,
    fetchEventTypesFailure,
    fetchStatusesRequest,
    fetchStatusesSuccess,
    fetchStatusesFailure,
    fetchEventDetail,
} from '/src/openapi/ccdb-bff/apis/EventDetailsApi';
import type {EventDetail} from '/src/openapi/ccdb-bff/models/EventDetail';
import type {EventType} from '/src/openapi/ccdb-bff/models/EventType';
import type {EventStatus, EventStatusResponse} from '/src/openapi/ccdb-bff/models/EventStatus';

import type {CcdbCompany} from '/src/openapi/ccdb-bff/models/CcdbCompany';
import type {CompanySearchResponse} from '/src/openapi/ccdb-bff/models/CompanySearchResponse';

import {ROOT} from '/src/middleware/api';

import {
    clearCompanySearch,
    clearEventMessages,
    DateConfidence,
    EventDetailsFormData,
    populateEventForm,
    resetEventForm,
    saveEventDetailsFailure,
    saveEventDetailsRequest,
    saveEventDetailsSuccess,
    setCompanySearchLoading,
    setCompanySearchResults,
    updateEventField,
} from './services/reducer';

// ──────── Default placeholder when API data is not yet loaded ────────

const EMPTY_SELECT_OPTION = [{value: '', label: '--Select--'}];

export const COUNTRY_OPTIONS = [
    {value: '', label: '--Select--'},
    {value: '45', label: 'United Kingdom'},
    {value: '46', label: 'United States'},
    {value: '55', label: 'Australia'},
    {value: '39', label: 'Spain'},
    {value: '21', label: 'India'},
    {value: '15', label: 'France'},
    {value: '16', label: 'Germany'},
];

export const TIME_ZONE_OPTIONS = [
    {value: '', label: '--Select--'},
    {value: '0', label: 'OTHERS - Unknown Time Zone'},
    {value: '113', label: 'UTC - Coordinated Universal Time'},
    {value: '41', label: 'CST - Central Standard Time (North America)'},
    {value: '30', label: 'CDT - Central Daylight Time (North America)'},
    {value: '5', label: 'AEST - Australian Eastern Standard Time'},
];

export const VALIDATION_SOURCE_OPTIONS = [
    {value: '0', label: '--Select--'},
    {value: '1', label: 'PRA'},
    {value: '2', label: 'Exchange'},
    {value: '3', label: 'WebCrawler'},
    {value: '4', label: 'Manual'},
    {value: '5', label: 'IR Alerts Mails'},
    {value: '6', label: 'RSS feed'},
];

export const INFO_TYPE_OPTIONS = [
    {value: '0', label: '--Select--'},
    {value: '6', label: 'Pre-Notification'},
    {value: '7', label: 'Guidance'},
];

export const IS_REPLAY_AVAILABLE_OPTIONS = [
    {value: '', label: '--Select--'},
    {value: '1', label: 'Yes'},
    {value: '2', label: 'No'},
];

export const DATE_CONFIDENCE_OPTIONS: {value: DateConfidence; label: string}[] = [
    {value: 'Estimated', label: 'Estimated'},
    {value: 'Approximate', label: 'Approximate'},
    {value: 'Confirmed', label: 'Confirmed'},
];

// ──────── Hook ────────

export const useEventDetails = () => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const eventId = (location.state as {eventId?: string} | null)?.eventId ?? null;
    const {
        formData,
        saving,
        error,
        successMessage,
        eventTypes,
        eventTypesLoading,
        statuses,
        statusesLoading,
        companySearchResults,
        companySearchLoading,
        eventDetailLoading,
        editingEventId,
        fetchedEventDetail,
    } = useAppSelector(state => state.ccdbEventDetails);

    // ──── Build dropdown options from API data ────

    const EVENT_TYPE_OPTIONS: Array<{value: string; label: string}> = eventTypes.length > 0
        ? [
            {value: '', label: '--Select--'},
            ...eventTypes.map(et => ({value: String(et.eventTypeId), label: et.eventType})),
        ]
        : EMPTY_SELECT_OPTION;

    const STATUS_OPTIONS: Array<{value: string; label: string}> = statuses.length > 0
        ? [
            {value: '', label: '--Select--'},
            ...statuses.map(s => ({value: String(s.id), label: s.status})),
        ]
        : EMPTY_SELECT_OPTION;

    // ──── Company Search ────

    const searchCompanies = useCallback(async (searchValue: string, searchField: 'COMPANY_NAME' | 'COMPANY_ID' = 'COMPANY_NAME') => {
        if (!searchValue.trim()) {
            dispatch(clearCompanySearch());
            return;
        }
        dispatch(setCompanySearchLoading(true));
        try {
            const params = new URLSearchParams({
                searchValue: searchValue.trim(),
                searchField,
                page: '0',
                size: '100',
            });
            const response = await fetch(`${ROOT}/api/companies/search?${params.toString()}`, {
                headers: {'Accept': 'application/json'},
            });
            if (!response.ok) {
                dispatch(setCompanySearchResults([]));
                return;
            }
            const data: CompanySearchResponse = await response.json();
            dispatch(setCompanySearchResults(data.companies ?? []));
        } catch {
            dispatch(setCompanySearchResults([]));
        }
    }, [dispatch]);

    const selectCompany = useCallback((company: CcdbCompany) => {
        dispatch(updateEventField({field: 'companyId', value: company.companyId}));
        dispatch(updateEventField({field: 'companyName', value: company.companyName}));
        dispatch(updateEventField({field: 'fiscalYearEnd', value: company.fiscalYearEndDate ?? ''}));
        dispatch(clearCompanySearch());
    }, [dispatch]);

    const clearCompanyResults = useCallback(() => {
        dispatch(clearCompanySearch());
    }, [dispatch]);

    // ──── Fetch dropdown data from API ────

    const fetchEventTypesData = useCallback(async () => {
        const meta = {};
        dispatch(fetchEventTypesRequest({data: undefined, meta, error: false}));

        try {
            const response = await fetch(`${ROOT}/api/event-types`, {
                headers: {'Accept': 'application/json'},
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({
                    code: response.status,
                    message: `Failed to fetch event types (HTTP ${response.status})`,
                }));
                dispatch(fetchEventTypesFailure({data: errorData, meta, error: true}));
                return;
            }

            const data: EventType[] = await response.json();
            dispatch(fetchEventTypesSuccess({data, meta, error: false}));
        } catch (err) {
            dispatch(fetchEventTypesFailure({
                data: {code: 0, message: err instanceof Error ? err.message : 'Network error occurred'},
                meta,
                error: true,
            }));
        }
    }, [dispatch]);

    const fetchStatusesData = useCallback(async () => {
        const meta = {};
        dispatch(fetchStatusesRequest({data: undefined, meta, error: false}));

        try {
            const response = await fetch(`${ROOT}/api/statuses`, {
                headers: {'Accept': 'application/json'},
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({
                    code: response.status,
                    message: `Failed to fetch statuses (HTTP ${response.status})`,
                }));
                dispatch(fetchStatusesFailure({data: errorData, meta, error: true}));
                return;
            }

            const responseData: EventStatusResponse = await response.json();
            const data: EventStatus[] = responseData.objectData ?? [];
            dispatch(fetchStatusesSuccess({data, meta, error: false}));
        } catch (err) {
            dispatch(fetchStatusesFailure({
                data: {code: 0, message: err instanceof Error ? err.message : 'Network error occurred'},
                meta,
                error: true,
            }));
        }
    }, [dispatch]);

    // ──── Load dropdown data on mount ────

    useEffect(() => {
        if (eventTypes.length === 0 && !eventTypesLoading) {
            fetchEventTypesData();
        }
    }, [eventTypes.length, eventTypesLoading, fetchEventTypesData]);

    useEffect(() => {
        if (statuses.length === 0 && !statusesLoading) {
            fetchStatusesData();
        }
    }, [statuses.length, statusesLoading, fetchStatusesData]);

    // ──── Fetch event detail by ID once dropdowns are loaded ────

    const eventDetailFetchedRef = useRef(false);

    useEffect(() => {
        if (
            !eventId ||
            eventTypes.length === 0 ||
            statuses.length === 0 ||
            eventDetailFetchedRef.current
        ) {
            return;
        }

        eventDetailFetchedRef.current = true;

        // Dispatch the CALL_API action — same architecture as fetchEventTypes / fetchStatuses
        dispatch(fetchEventDetail({eventId}) as any);
    }, [eventId, eventTypes, statuses, dispatch]);

    // ──── Populate form when fetchedEventDetail arrives ────

    useEffect(() => {
        if (!fetchedEventDetail || eventTypes.length === 0) {
            return;
        }

        const data: EventDetail = fetchedEventDetail;

        // Helper: parse ISO date string to MM/DD/YYYY
        const formatDate = (isoStr: string | null): string => {
            if (!isoStr) return '';
            try {
                const d = new Date(isoStr);
                if (isNaN(d.getTime())) return '';
                const mm = String(d.getMonth() + 1).padStart(2, '0');
                const dd = String(d.getDate()).padStart(2, '0');
                const yyyy = d.getFullYear();
                return `${mm}/${dd}/${yyyy}`;
            } catch {
                return '';
            }
        };

        // Match eventType label from API to eventTypeId for the dropdown
        const matchedEventType = eventTypes.find(
            et => et.eventType === data.eventType,
        );
        const eventTypeValue = matchedEventType
            ? String(matchedEventType.eventTypeId)
            : '';

        // Status from API is the ID as string — use directly
        const statusValue = data.status ?? '';

        // TimeZone from API is timeZoneId — use as string for dropdown
        const timeZoneValue = data.timeZoneId != null
            ? String(data.timeZoneId)
            : '';

        // Populate all form fields from the API response
        dispatch(populateEventForm({
            companyId: data.companyId ?? '',
            companyName: data.companyName ?? '',
            eventName: data.eventName ?? '',
            eventType: eventTypeValue,
            effectiveBeginDate: formatDate(data.effectiveBeginningDate),
            effectiveBeginTime: data.time ?? '',
            effectiveEndDate: formatDate(data.effectiveEndDate),
            eventYear: data.eventYear != null ? String(data.eventYear) : '',
            eventDetailUrl: data.eventUrl ?? '',
            webcastUrl: data.webcastUrl ?? '',
            status: statusValue,
            notes: data.notes ?? '',
            fiscalYearEnd: data.fiscalYearEnd ?? '',
            timeZone: timeZoneValue,
            country: data.country ?? '',
            validationSource: data.validationSource ?? '',
            infoType: data.infoType ?? '',
            tollFree: data.tollFree ?? '',
            tollNumber: data.tollNumber ?? '',
            passCode: data.passCode ?? '',
            replayTollFree: data.replayTollFree ?? '',
            replayTollNumber: data.replayTollNumber ?? '',
            replayPassCode: data.replayPassCode ?? '',
            isReplayAvailable: data.isReplayAvailable ?? '',
            replayExpiryDate: formatDate(data.replayExpiryDate),
            creationDate: formatDate(data.creationDate) || formData.creationDate,
            holidayRemark: data.holidayRemark ?? '',
            dateConfidence: (data.eventDateStatus as DateConfidence) || 'Estimated',
        }));
    }, [fetchedEventDetail, eventTypes, dispatch, formData.creationDate]);

    const setField = useCallback(<K extends keyof EventDetailsFormData>(field: K, value: EventDetailsFormData[K]) => {
        dispatch(updateEventField({field, value}));
    }, [dispatch]);

    const handleReset = useCallback(() => {
        dispatch(resetEventForm());
    }, [dispatch]);

    const handleClearMessages = useCallback(() => {
        dispatch(clearEventMessages());
    }, [dispatch]);

    const handleSave = useCallback(async () => {
        dispatch(saveEventDetailsRequest());

        try {
            const response = await fetch(`${ROOT}/api/event-details`, {
                method: 'POST',
                headers: {'Content-Type': 'application/json', Accept: 'application/json'},
                body: JSON.stringify({
                    companyId: formData.companyId,
                    eventName: formData.eventName,
                    eventStandardName: formData.eventStandardName,
                    eventType: formData.eventType,
                    effectiveBeginDate: formData.effectiveBeginDate,
                    effectiveBeginTime: formData.effectiveBeginTime,
                    dateConfidence: formData.dateConfidence,
                    effectiveEndDate: formData.effectiveEndDate,
                    effectiveEndTime: formData.effectiveEndTime,
                    fiscalYearEnd: formData.fiscalYearEnd,
                    eventYear: formData.eventYear,
                    eventDetailUrl: formData.eventDetailUrl,
                    webcastUrl: formData.webcastUrl,
                    tollFree: formData.tollFree,
                    tollNumber: formData.tollNumber,
                    passCode: formData.passCode,
                    replayTollFree: formData.replayTollFree,
                    replayTollNumber: formData.replayTollNumber,
                    replayPassCode: formData.replayPassCode,
                    isReplayAvailable: formData.isReplayAvailable,
                    replayExpiryDate: formData.replayExpiryDate,
                    country: formData.country,
                    timeZone: formData.timeZone,
                    validationSource: formData.validationSource,
                    infoType: formData.infoType,
                    status: formData.status,
                    notes: formData.notes,
                    holidayRemark: formData.holidayRemark,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({message: `Save failed (HTTP ${response.status})`}));
                dispatch(saveEventDetailsFailure(errorData.message || 'Failed to save event details'));
                return;
            }

            dispatch(saveEventDetailsSuccess('Event details saved successfully.'));
        } catch (err) {
            dispatch(saveEventDetailsFailure(err instanceof Error ? err.message : 'Network error occurred'));
        }
    }, [dispatch, formData]);

    return {
        formData,
        saving,
        error,
        successMessage,
        setField,
        handleSave,
        handleReset,
        handleClearMessages,
        EVENT_TYPE_OPTIONS,
        STATUS_OPTIONS,
        eventTypesLoading,
        statusesLoading,
        fetchEventTypesData,
        fetchStatusesData,
        companySearchResults,
        companySearchLoading,
        searchCompanies,
        selectCompany,
        clearCompanyResults,
        eventDetailLoading,
        editingEventId,
        eventId,
    };
};
